/**
 * Local persistence layer.
 * Versioned schema with migration support.
 * All data is localStorage/IndexedDB — no accounts, no tracking.
 */

const STORAGE_KEY = 'cts-v2';
const SCHEMA_VERSION = 1;

const DEFAULT_DATA = {
  version: SCHEMA_VERSION,
  settings: {
    soundEnabled: false,
    toastNotifications: true,
    connectionPreview: true,
    lastGameOptions: null,
  },
  stats: {
    gamesPlayed: 0,
    gamesWon: 0,
    bestScores: {},     // mode -> best score
    totalHintsUsed: 0,
  },
  daily: {
    streak: 0,
    maxStreak: 0,
    lastPlayedDate: null,
    history: {},        // dateString -> { score, hintsUsed, pathString }
  },
  collector: {
    actors: {},         // actorId -> { count, firstSeen }
    movies: {},         // movieId -> { count, firstSeen }
    tv: {},             // tvId -> { count, firstSeen }
  },
  affinity: {
    actors: {},         // actorId -> usage count
    movies: {},         // movieId -> usage count
    genres: {},         // genreId -> usage count
    decades: {},        // decade -> usage count
  },
  recentTargets: [],    // last 20 target person IDs
  favoriteTargets: [],  // favorited actor IDs
  recentGames: [],      // last 10 game summaries
  autosave: null,       // in-progress game state
  banList: {
    actors: [16828],
    titles: [{ type: 'tv', id: 27023 }, { type: 'tv', id: 456 }],
  },
};

function loadRaw() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveRaw(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Storage full — non-critical
  }
}

function migrate(data) {
  if (!data || !data.version) return { ...DEFAULT_DATA };

  // Future migrations go here
  // if (data.version < 2) { ... data.version = 2; }

  return data;
}

/** Load all stored data, with migration */
export function loadData() {
  const raw = loadRaw();
  if (!raw) return { ...DEFAULT_DATA };
  return migrate({ ...DEFAULT_DATA, ...raw });
}

/** Save all data */
export function saveData(data) {
  saveRaw(data);
}

/** Get a specific section */
export function getSection(key) {
  const data = loadData();
  return data[key];
}

/** Update a specific section */
export function updateSection(key, updater) {
  const data = loadData();
  if (typeof updater === 'function') {
    data[key] = updater(data[key]);
  } else {
    data[key] = updater;
  }
  saveRaw(data);
  return data[key];
}

/** Save auto-save state */
export function saveAutosave(gameState) {
  updateSection('autosave', gameState);
}

/** Clear auto-save */
export function clearAutosave() {
  updateSection('autosave', null);
}

/** Get auto-save */
export function getAutosave() {
  return getSection('autosave');
}

/** Record a completed game */
export function recordGame(gameSummary) {
  const data = loadData();

  // Update stats
  data.stats.gamesPlayed++;
  if (gameSummary.won) data.stats.gamesWon++;
  data.stats.totalHintsUsed += gameSummary.hintsUsed || 0;

  // Update recent games
  data.recentGames.unshift({
    ...gameSummary,
    date: new Date().toISOString(),
  });
  if (data.recentGames.length > 10) data.recentGames = data.recentGames.slice(0, 10);

  // Update recent targets
  for (const target of (gameSummary.targets || [])) {
    const tid = target.id || target;
    data.recentTargets = data.recentTargets.filter(t => t !== tid);
    data.recentTargets.unshift(tid);
  }
  if (data.recentTargets.length > 20) data.recentTargets = data.recentTargets.slice(0, 20);

  // Update collector and affinity if won
  if (gameSummary.won && gameSummary.pathNodeIds) {
    const now = new Date().toISOString();
    for (const nodeId of gameSummary.pathNodeIds) {
      const numId = parseInt(nodeId.slice(1), 10);
      if (nodeId.startsWith('a')) {
        if (!data.collector.actors[numId]) data.collector.actors[numId] = { count: 0, firstSeen: now };
        data.collector.actors[numId].count++;
        data.affinity.actors[numId] = (data.affinity.actors[numId] || 0) + 1;
      } else if (nodeId.startsWith('m')) {
        if (!data.collector.movies[numId]) data.collector.movies[numId] = { count: 0, firstSeen: now };
        data.collector.movies[numId].count++;
        data.affinity.movies[numId] = (data.affinity.movies[numId] || 0) + 1;
      } else if (nodeId.startsWith('t')) {
        if (!data.collector.tv[numId]) data.collector.tv[numId] = { count: 0, firstSeen: now };
        data.collector.tv[numId].count++;
      }
    }
  }

  saveRaw(data);
}

/** Record daily challenge result */
export function recordDaily(dateStr, result) {
  const data = loadData();

  data.daily.history[dateStr] = result;
  data.daily.lastPlayedDate = dateStr;

  // Update streak
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setUTCDate(yesterday.getUTCDate() - 1);
  const yStr = yesterday.toISOString().split('T')[0];

  if (data.daily.history[yStr] || data.daily.streak === 0) {
    data.daily.streak++;
  } else {
    data.daily.streak = 1;
  }
  data.daily.maxStreak = Math.max(data.daily.maxStreak, data.daily.streak);

  saveRaw(data);
}

/** Toggle favorite target */
export function toggleFavorite(actorId) {
  const data = loadData();
  const idx = data.favoriteTargets.indexOf(actorId);
  if (idx >= 0) {
    data.favoriteTargets.splice(idx, 1);
  } else {
    data.favoriteTargets.push(actorId);
  }
  saveRaw(data);
  return data.favoriteTargets;
}

/** Export all data as JSON string */
export function exportData() {
  return JSON.stringify(loadData(), null, 2);
}

/** Import data from JSON string */
export function importData(jsonString) {
  try {
    const data = JSON.parse(jsonString);
    if (!data.version) throw new Error('Invalid data format');
    saveRaw(migrate(data));
    return true;
  } catch {
    return false;
  }
}
